/**
 * Pen tool for "Draw on Path": click adds a corner anchor (L), click+drag a smooth one (C).
 * Mixed onto fabric.BoundedText.prototype, same pattern as TextPathEditor.js.
 */
const { TEXT_PATH_EDITOR_BOUNDS_MARGIN } = require('./TextPathEditor');

// Screen px. Below DRAG a press counts as a plain click, within CLOSE of the last anchor it ends the path
const PEN_DRAG_THRESHOLD = 4;
const PEN_CLOSE_THRESHOLD = 6;
const PEN_COLOR = '#5181FF';

// Anchors are { x, y, in, out } in the same unit space textPath is stored in (in/out are bezier handles or null)
const penAnchorsToCommands = (anchors) =>
    anchors.map((anchor, index) => {
        if (index === 0) {
            return ['M', anchor.x, anchor.y];
        }
        const prev = anchors[index - 1];
        if (!prev.out && !anchor.in) {
            return ['L', anchor.x, anchor.y];
        }
        const control1 = prev.out || prev;
        const control2 = anchor.in || anchor;
        return ['C', control1.x, control1.y, control2.x, control2.y, anchor.x, anchor.y];
    });

const isTypingTarget = (target) =>
    !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

const textPathPen = {
    /**
     * Starts drawing a new path from scratch. The current path stays until the new one has 2 anchors.
     * Ends on Enter/Escape, a click on the last anchor (so double click too) or deselection.
     * @param {Object} [options]
     * @param {Function} [options.onFinish] called with the element once the pen ends, however it ends
     */
    startTextPathPen({ onFinish } = {}) {
        if (!this.canvas || this.isDrawingTextPath) {
            return;
        }
        if (this.isEditingTextPath) {
            this.disableTextPathEditor();
        }

        const canvas = this.canvas;
        const pen = {
            anchors: [],
            pointer: null,
            isDragging: false,
            onFinish,
            previousPath: this.item(0).textPath,
            originalControls: this.controls,
            canvasBackup: {
                selection: canvas.selection,
                skipTargetFind: canvas.skipTargetFind,
                defaultCursor: canvas.defaultCursor
            }
        };
        pen.handlers = {
            'mouse:down': (opt) => this._onTextPathPenDown(opt.e),
            'mouse:move': (opt) => this._onTextPathPenMove(opt.e),
            'mouse:up': () => {
                pen.isDragging = false;
            }
        };
        pen.onKeyDown = (e) => this._onTextPathPenKeyDown(e);
        this._textPathPen = pen;

        // Every click has to reach the pen instead of moving/selecting objects or drawing a selection box
        canvas.selection = false;
        canvas.skipTargetFind = true;
        canvas.defaultCursor = 'crosshair';
        canvas.setCursor('crosshair');
        // A click with no target makes fabric discard the selection, which would end the pen on every point
        this.onDeselect = (opt) => !!(opt && opt.e && opt.e.target === canvas.upperCanvasEl);

        Object.keys(pen.handlers).forEach((eventName) => canvas.on(eventName, pen.handlers[eventName]));
        // Capture phase, to run before the studio's own shortcuts (Delete would remove the element)
        window.addEventListener('keydown', pen.onKeyDown, true);

        this.controls = { textPathPen: this._createTextPathPenControl() };
        this.isDrawingTextPath = true;
        this.setTextPathDebug(false);
        canvas.setActiveObject(this);
        canvas.requestRenderAll();
    },

    /**
     * @param {Object} [options]
     * @param {boolean} [options.edit=true] open the point editor on the drawn path. It selects the
     *   element, so it must be false while the element is being deselected
     */
    finishTextPathPen({ edit = true } = {}) {
        const pen = this._textPathPen;
        if (!pen) {
            return;
        }
        const canvas = this.canvas;
        if (canvas) {
            Object.keys(pen.handlers).forEach((eventName) => canvas.off(eventName, pen.handlers[eventName]));
            canvas.selection = pen.canvasBackup.selection;
            canvas.skipTargetFind = pen.canvasBackup.skipTargetFind;
            canvas.defaultCursor = pen.canvasBackup.defaultCursor;
            canvas.setCursor(canvas.defaultCursor);
        }
        window.removeEventListener('keydown', pen.onKeyDown, true);
        delete this.onDeselect;

        this.controls = pen.originalControls;
        this._textPathPen = null;
        this.isDrawingTextPath = false;
        this.set('dirty', true);

        if (edit && canvas && this.item(0).textPath) {
            this.enableTextPathEditor();
        } else if (canvas) {
            canvas.requestRenderAll();
        }
        pen.onFinish?.(this);
    },

    toggleTextPathPen(options) {
        if (this.isDrawingTextPath) {
            this.finishTextPathPen();
        } else {
            this.startTextPathPen(options);
        }
    },

    _getTextPathScale() {
        return Math.min(this.item(0).maxWidth, this.item(0).maxHeight) || 1;
    },

    _textPathPenToScreen(point) {
        const pathScale = this._getTextPathScale();
        const matrix = fabric.util.multiplyTransformMatrices(this.canvas.viewportTransform, this.calcTransformMatrix());
        return fabric.util.transformPoint({ x: point.x * pathScale, y: point.y * pathScale }, matrix);
    },

    // Same limits the point editor applies while dragging (see its actionHandler for the why)
    _getTextPathPenPoint(e, isAnchor) {
        const pointer = this.canvas.getPointer(e);
        const local = fabric.util.transformPoint(pointer, fabric.util.invertTransform(this.calcTransformMatrix()));
        const halfWidth = isAnchor ? (this.item(0).maxWidth / 2) * (1 + TEXT_PATH_EDITOR_BOUNDS_MARGIN) : this.width;
        const halfHeight = isAnchor ? (this.item(0).maxHeight / 2) * (1 + TEXT_PATH_EDITOR_BOUNDS_MARGIN) : this.height;
        const pathScale = this._getTextPathScale();
        return {
            x: Math.min(Math.max(local.x, -halfWidth), halfWidth) / pathScale,
            y: Math.min(Math.max(local.y, -halfHeight), halfHeight) / pathScale
        };
    },

    _getTextPathPenScreenDistance(pointA, pointB) {
        const a = this._textPathPenToScreen(pointA);
        const b = this._textPathPenToScreen(pointB);
        return Math.hypot(a.x - b.x, a.y - b.y);
    },

    _applyTextPathPen() {
        const { anchors, previousPath } = this._textPathPen;
        // A path needs 2 anchors, until then the text keeps following the previous one
        const pathData = anchors.length >= 2 ? fabric.util.joinPath(penAnchorsToCommands(anchors)) : previousPath;
        this.setTextPath(pathData, this.item(0).textPathAutoFit !== false, false, { normalize: false });
    },

    _onTextPathPenDown(e) {
        const pen = this._textPathPen;
        if (!pen || e.button > 0) {
            return;
        }
        const point = this._getTextPathPenPoint(e, true);
        const last = pen.anchors[pen.anchors.length - 1];
        if (last && this._getTextPathPenScreenDistance(last, point) < PEN_CLOSE_THRESHOLD) {
            this.finishTextPathPen();
            return;
        }
        pen.anchors.push({ x: point.x, y: point.y, in: null, out: null });
        pen.pointer = point;
        pen.isDragging = true;
        this._applyTextPathPen();
    },

    _onTextPathPenMove(e) {
        const pen = this._textPathPen;
        if (!pen) {
            return;
        }
        pen.pointer = this._getTextPathPenPoint(e, true);
        if (pen.isDragging) {
            const anchor = pen.anchors[pen.anchors.length - 1];
            const handle = this._getTextPathPenPoint(e, false);
            if (anchor.out || this._getTextPathPenScreenDistance(anchor, handle) > PEN_DRAG_THRESHOLD) {
                anchor.out = handle;
                // Mirrored handle keeps the curve smooth through the anchor. The first anchor has no incoming segment
                anchor.in = pen.anchors.length > 1 ? { x: 2 * anchor.x - handle.x, y: 2 * anchor.y - handle.y } : null;
                this._applyTextPathPen();
            }
        }
        this.canvas.requestRenderAll();
    },

    _onTextPathPenKeyDown(e) {
        const pen = this._textPathPen;
        if (!pen || isTypingTarget(e.target)) {
            return;
        }
        if (e.key === 'Enter' || e.key === 'Escape') {
            this.finishTextPathPen();
        } else if (e.key === 'Backspace' || e.key === 'Delete') {
            pen.anchors.pop();
            pen.isDragging = false;
            this._applyTextPathPen();
        } else {
            return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
    },

    // Paint-only control (same idea as the editor's pathGuideLines): the path being drawn, its handles and anchors
    _createTextPathPenControl() {
        return new fabric.Control({
            skipTargetFind: true,
            render: function (ctx, left, top, styleOverride, fabricObject) {
                const pen = fabricObject._textPathPen;
                if (!pen || !pen.anchors.length) {
                    return;
                }
                const toScreen = (point) => fabricObject._textPathPenToScreen(point);
                const anchors = pen.anchors;
                const last = anchors[anchors.length - 1];

                ctx.save();
                ctx.strokeStyle = PEN_COLOR;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                penAnchorsToCommands(anchors).forEach((command) => {
                    if (command[0] === 'M') {
                        const point = toScreen({ x: command[1], y: command[2] });
                        ctx.moveTo(point.x, point.y);
                    } else if (command[0] === 'L') {
                        const point = toScreen({ x: command[1], y: command[2] });
                        ctx.lineTo(point.x, point.y);
                    } else {
                        const control1 = toScreen({ x: command[1], y: command[2] });
                        const control2 = toScreen({ x: command[3], y: command[4] });
                        const point = toScreen({ x: command[5], y: command[6] });
                        ctx.bezierCurveTo(control1.x, control1.y, control2.x, control2.y, point.x, point.y);
                    }
                });
                // Preview of the segment the next click would add
                if (!pen.isDragging && pen.pointer) {
                    const pointer = toScreen(pen.pointer);
                    if (last.out) {
                        const control1 = toScreen(last.out);
                        ctx.bezierCurveTo(control1.x, control1.y, pointer.x, pointer.y, pointer.x, pointer.y);
                    } else {
                        ctx.lineTo(pointer.x, pointer.y);
                    }
                }
                ctx.stroke();

                // Only the last anchor's handles, like Figma: the rest can't be changed from the pen anyway
                ctx.lineWidth = 1;
                [last.in, last.out].forEach((handle) => {
                    if (!handle) {
                        return;
                    }
                    const anchorPoint = toScreen(last);
                    const handlePoint = toScreen(handle);
                    ctx.beginPath();
                    ctx.moveTo(anchorPoint.x, anchorPoint.y);
                    ctx.lineTo(handlePoint.x, handlePoint.y);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.fillStyle = PEN_COLOR;
                    ctx.arc(handlePoint.x, handlePoint.y, 3, 0, Math.PI * 2);
                    ctx.fill();
                });

                ctx.lineWidth = 1.5;
                ctx.fillStyle = '#ffffff';
                anchors.forEach((anchor) => {
                    const point = toScreen(anchor);
                    ctx.beginPath();
                    ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.stroke();
                });
                ctx.restore();
            }
        });
    }
};

fabric.util.object.extend(fabric.BoundedText.prototype, textPathPen);
